import type { SimCtx } from '../context';
import { AFFIX_BITS } from '../../content/affixes';
import { EL_FIRE, EL_FROST, EL_LIGHTNING, EL_TEMPORAL, SIDE_ENEMY, SIDE_PLAYER, wrapDelta } from '../world';
import { killEnemy } from './enemies';
import { datan2, dcos, dhypot, dsin } from '../../core/dmath';
import type { ProjectileEventVisual } from '../../core/events';

const PLAYER_RADIUS = 0.5;
const CONTACT_COOLDOWN = 0.8;
/** 'leeching' affix: fraction of contact damage dealt returned as heal (bullet damage isn't attributable to an enemy slot, see CONTENT_GUIDE). */
const AFFIX_LEECH_MULT = 3;

/**
 * DAMAGE MATH (player → enemy)
 *   base   = stats.damage × stats.damageMult × weaponBullet.damage × stillBonus
 *   crit   = rng < critChance ? × critMult : ×1
 *   fire   : chance → burn fireDps for 3s (refreshes)
 *   frost  : chance → slow by frostSlow for 2s
 *   lightning: chance → chain to `lightningChains` nearest enemies for 50% dmg
 *   temporal : chance → slow by temporalSlow for 1.5s (stacks with frost as max)
 * Enemy → player: dmg = max(1, bulletDamage − armor); i-frames 0.5s after any hit.
 */
export function updateCollisions(ctx: SimCtx, dt: number): void {
  const w = ctx.w;
  const g = w.grid;
  const p = w.projectiles;
  const e = w.enemies;
  const half = w.half;

  g.build(e);

  // 1. player projectiles vs enemies (grid) ; 2. enemy projectiles vs players (direct)
  const high = p.pool.high;
  for (let i = 1; i < high; i++) {
    if (!p.pool.alive[i]) continue;
    const x = p.x[i];
    const y = p.y[i];
    const r = p.radius[i];
    if (p.side[i] === SIDE_PLAYER) {
      g.prepare(x, y, r + 1.5);
      let consumed = false;
      for (let cy = g.qy0; cy <= g.qy1 && !consumed; cy++) {
        for (let cx = g.qx0; cx <= g.qx1 && !consumed; cx++) {
          const c = g.wrapRow(cy) * g.cols + g.wrapCol(cx);
          const s = g.cellStart[c];
          const en = g.cellStart[c + 1];
          for (let k = s; k < en; k++) {
            const j = g.items[k];
            if (!e.pool.alive[j] || j === p.lastHit[i] || Math.abs(e.z[j]) > 0.5 || e.invuln[j] > 0) continue;
            const dx = wrapDelta(e.x[j] - x, half);
            const dy = wrapDelta(e.y[j] - y, half);
            const rr = r + e.radius[j];
            if (dx * dx + dy * dy > rr * rr) continue;
            hitEnemy(ctx, i, j);
            p.lastHit[i] = j;
            if (p.pierce[i] > 0) {
              p.pierce[i]--;
            } else if (p.bounce[i] > 0) {
              p.bounce[i]--;
              // bounce: redirect toward another nearby enemy or reflect
              const a = datan2(-dy, -dx) + ctx.rng.range(-0.6, 0.6);
              const sp = dhypot(p.vx[i], p.vy[i]);
              p.vx[i] = dcos(a) * sp;
              p.vy[i] = dsin(a) * sp;
            } else {
              p.pool.release(i);
              consumed = true;
              break;
            }
          }
        }
      }
    } else {
      for (let pi = 0; pi < w.players.length; pi++) {
        const pl = w.players[pi];
        if (!pl.alive || pl.iframes > 0) continue;
        const dx = wrapDelta(pl.x - x, half);
        const dy = wrapDelta(pl.y - y, half);
        const rr = r + PLAYER_RADIUS;
        if (dx * dx + dy * dy <= rr * rr) {
          damagePlayer(ctx, pi, p.damage[i], false, { color: p.color[i], visual: p.visual[i], elements: p.elements[i] });
          p.pool.release(i);
          break;
        }
      }
    }
  }

  // 3. enemies vs players (contact)
  for (let pi = 0; pi < w.players.length; pi++) {
    const pl = w.players[pi];
    if (!pl.alive) continue;
    g.prepare(pl.x, pl.y, 3);
    for (let cy = g.qy0; cy <= g.qy1; cy++) {
      for (let cx = g.qx0; cx <= g.qx1; cx++) {
        const c = g.wrapRow(cy) * g.cols + g.wrapCol(cx);
        const s = g.cellStart[c];
        const en = g.cellStart[c + 1];
        for (let k = s; k < en; k++) {
          const j = g.items[k];
          if (!e.pool.alive[j] || Math.abs(e.z[j]) > 0.5 || e.contactCd[j] > 0) continue;
          const dx = wrapDelta(e.x[j] - pl.x, half);
          const dy = wrapDelta(e.y[j] - pl.y, half);
          const rr = e.radius[j] + PLAYER_RADIUS;
          if (dx * dx + dy * dy > rr * rr) continue;
          // Damage grace is also contact grace: a dash passes cleanly through a crowd
          // and one hit cannot let other overlapping enemies kick the player sideways.
          if (pl.iframes > 0) continue;
          e.contactCd[j] = CONTACT_COOLDOWN;
          const dmg = e.isBoss[j] && ctx.boss ? ctx.boss.def.damage : ctx.reg.enemyList[e.def[j]].damage * (e.elite[j] ? 1.5 : 1);
          if (pl.iframes <= 0) {
            damagePlayer(ctx, pi, dmg * ctx.scale.damage);
            // 'leeching' affix: heal the elite for 3x the contact damage it just dealt (capped at max HP)
            if (!ctx.godMode && e.elite[j] && e.affixes[j] & AFFIX_BITS.leeching) {
              e.hp[j] = Math.min(e.maxHp[j], e.hp[j] + dmg * ctx.scale.damage * AFFIX_LEECH_MULT);
            }
          }
          // knock the player slightly away for readability
          const d = Math.sqrt(dx * dx + dy * dy) || 1;
          pl.x -= (dx / d) * 0.3;
          pl.y -= (dy / d) * 0.3;
        }
      }
    }
  }

  // 4. hazards vs players (continuous dps) and vs enemies (player-side hazards)
  const h = w.hazards;
  for (let i = 1; i < h.pool.high; i++) {
    if (!h.pool.alive[i] || h.armT[i] > 0) continue; // telegraph phase: visible, harmless
    const r2 = h.radius[i] * h.radius[i];
    if (h.side[i] === SIDE_ENEMY) {
      for (let pi = 0; pi < w.players.length; pi++) {
        const pl = w.players[pi];
        if (!pl.alive || pl.iframes > 0) continue;
        const dx = wrapDelta(pl.x - h.x[i], half);
        const dy = wrapDelta(pl.y - h.y[i], half);
        if (dx * dx + dy * dy <= r2) damagePlayer(ctx, pi, h.dps[i] * ctx.scale.hazardDamage * dt, true);
      }
    } else {
      g.prepare(h.x[i], h.y[i], h.radius[i]);
      for (let cy = g.qy0; cy <= g.qy1; cy++) {
        for (let cx = g.qx0; cx <= g.qx1; cx++) {
          const c = g.wrapRow(cy) * g.cols + g.wrapCol(cx);
          for (let k = g.cellStart[c]; k < g.cellStart[c + 1]; k++) {
            const j = g.items[k];
            if (!e.pool.alive[j]) continue;
            const dx = wrapDelta(e.x[j] - h.x[i], half);
            const dy = wrapDelta(e.y[j] - h.y[i], half);
            if (dx * dx + dy * dy <= r2) {
              e.hp[j] -= h.dps[i] * dt;
              if (e.hp[j] <= 0) killEnemy(ctx, j, -1);
            }
          }
        }
      }
    }
  }
}

function hitEnemy(ctx: SimCtx, pi: number, j: number): void {
  const p = ctx.w.projectiles;
  const e = ctx.w.enemies;
  const owner = p.owner[pi];
  const s = ctx.stats[owner];
  const projectile = { color: p.color[pi], visual: p.visual[pi], elements: p.elements[pi] };
  let dmg = p.damage[pi] * p.stillBonus[pi];
  // 'shielded' affix: while the shield holds, the hit is absorbed and never crits/procs elements.
  const shielded = (e.affixes[j] & AFFIX_BITS.shielded) !== 0 && e.shield[j] > 0;
  const warded = (e.affixes[j] & AFFIX_BITS.warded) !== 0;
  let crit = false;
  if (!shielded) {
    crit = ctx.rng.chance(p.critChance[pi]);
    if (crit) dmg *= p.critMult[pi];
    const el = p.elements[pi];
    if (el) {
      // 'warded' affix: immune to frost/fire/temporal status (lightning's chain damage still applies).
      if (!warded) {
        if (el & EL_FIRE && ctx.rng.chance(s.fireChance)) {
          e.burnDps[j] = Math.max(e.burnDps[j], s.fireDps * s.damageMult);
          e.burnT[j] = 3;
        }
        if (el & EL_FROST && ctx.rng.chance(s.frostChance)) {
          e.slow[j] = Math.max(e.slow[j], s.frostSlow);
          e.slowT[j] = Math.max(e.slowT[j], 2);
        }
        if (el & EL_TEMPORAL && ctx.rng.chance(s.temporalChance)) {
          e.slow[j] = Math.max(e.slow[j], s.temporalSlow);
          e.slowT[j] = Math.max(e.slowT[j], 1.5);
        }
      }
      if (el & EL_LIGHTNING && ctx.rng.chance(s.lightningChance)) chainLightning(ctx, j, dmg * 0.5, s.lightningChains, owner);
    }
  }
  // 'shieldWall' behavior: hits landing inside the frontal arc are largely absorbed — get behind it.
  if (!e.isBoss[j]) {
    const bh = ctx.reg.enemyList[e.def[j]]?.behavior;
    if (bh && bh.type === 'shieldWall') {
      const ang = datan2(-p.vy[pi], -p.vx[pi]); // direction the bullet came FROM
      let d = ang - e.facing[j];
      d = datan2(dsin(d), dcos(d));
      if (Math.abs(d) < bh.frontArc * 0.5) {
        dmg *= 1 - bh.frontReduction;
        ctx.events.push({ type: 'sfx', id: 'block', x: e.x[j], y: e.y[j] });
      }
    }
  }
  // 'subsystems' boss special: damage is reduced while at least one shootable pod still lives.
  if (e.isBoss[j] && ctx.boss) {
    const special = ctx.boss.def.phases[e.phase[j]].special;
    if (special?.type === 'subsystems' && e.shieldAdds[j] > 0) {
      dmg *= 1 - special.reduction;
      ctx.events.push({ type: 'sfx', id: 'block', x: e.x[j], y: e.y[j] });
    }
  }

  // light knockback along the bullet's travel direction (bosses/tanks resist)
  const kb = e.isBoss[j] ? 0 : 0.35 / Math.max(0.5, e.radius[j]);
  if (kb > 0) {
    const sp = dhypot(p.vx[pi], p.vy[pi]) || 1;
    e.x[j] += (p.vx[pi] / sp) * kb;
    e.y[j] += (p.vy[pi] / sp) * kb;
  }
  if (shielded) {
    e.shield[j] -= dmg;
    ctx.events.push({ type: 'hit', x: e.x[j], y: e.y[j], dmg, crit: false, side: 0, projectile });
    if (e.shield[j] <= 0) {
      const overflow = -e.shield[j];
      e.shield[j] = 0;
      ctx.events.push({ type: 'sfx', id: 'shieldBreak', x: e.x[j], y: e.y[j] });
      if (overflow > 0) applyDamage(ctx, j, overflow, owner, false, projectile);
    }
    return;
  }
  applyDamage(ctx, j, dmg, owner, crit, projectile);
}

function applyDamage(ctx: SimCtx, j: number, dmg: number, owner: number, crit: boolean, projectile?: { color: number; visual: number; elements: number }): void {
  const e = ctx.w.enemies;
  const s = ctx.stats[owner];
  const pl = ctx.w.players[owner];
  e.hp[j] -= dmg;
  e.hitFlash[j] = 0.08;
  pl.damageDealt += dmg;
  if (s.lifesteal > 0) pl.hp = Math.min(s.maxHp, pl.hp + dmg * s.lifesteal);
  ctx.events.push({ type: 'hit', x: e.x[j], y: e.y[j], dmg, crit, side: 0, projectile });
  if (e.hp[j] <= 0) killEnemy(ctx, j, owner);
}

function chainLightning(ctx: SimCtx, from: number, dmg: number, chains: number, owner: number): void {
  const e = ctx.w.enemies;
  let cur = from;
  let guard = 0;
  const visited = ctx._chainVisited;
  visited.fill(0);
  visited[from] = 1;
  while (chains-- > 0 && guard++ < 16) {
    let best = 36; // 6 units
    let next = 0;
    const g = ctx.w.grid;
    g.prepare(e.x[cur], e.y[cur], 6);
    for (let cy = g.qy0; cy <= g.qy1; cy++) {
      for (let cx = g.qx0; cx <= g.qx1; cx++) {
        const c = g.wrapRow(cy) * g.cols + g.wrapCol(cx);
        for (let k = g.cellStart[c]; k < g.cellStart[c + 1]; k++) {
          const j = g.items[k];
          if (!e.pool.alive[j] || visited[j]) continue;
          const dx = wrapDelta(e.x[j] - e.x[cur], ctx.w.half);
          const dy = wrapDelta(e.y[j] - e.y[cur], ctx.w.half);
          const d2 = dx * dx + dy * dy;
          if (d2 < best) {
            best = d2;
            next = j;
          }
        }
      }
    }
    if (next === 0) break;
    visited[next] = 1;
    ctx.events.push({ type: 'sfx', id: 'chain', x: e.x[next], y: e.y[next] });
    applyDamage(ctx, next, dmg, owner, false);
    cur = next;
  }
}

export function damagePlayer(ctx: SimCtx, pi: number, raw: number, continuous = false, projectile?: ProjectileEventVisual): void {
  const pl = ctx.w.players[pi];
  const s = ctx.stats[pi];
  if (!pl.alive) return;
  if (ctx.godMode) return;
  if (pl.keystoneShield) {
    pl.keystoneShield = false;
    pl.iframes = Math.max(pl.iframes, 0.2);
    ctx.events.push({ type: 'sfx', id: 'shieldBreak', x: pl.x, y: pl.y });
    return;
  }
  const dmg = continuous ? Math.max(0, raw - s.armor * 0.016) : Math.max(1, raw - s.armor);
  pl.hp -= dmg;
  pl.damageTaken += dmg;
  if (!continuous) {
    pl.iframes = 0.5;
    pl.hitFlash = 0.2;
    ctx.events.push({ type: 'playerHit', player: pi, dmg, projectile });
    ctx.events.push({ type: 'shake', strength: 0.35 });
  }
  if (pl.hp <= 0) {
    pl.hp = 0;
    pl.alive = false;
    ctx.events.push({ type: 'playerDeath', player: pi });
  }
}
